import React, { useMemo, useState } from "react";
import moment from "moment";
import { MdOutlineExpandMore } from "react-icons/md";
import { formatAmountWithCurrency } from "../../../../../../utils/CurrencyUtils";
import ArrowElbow from "../../../../../../assets/images/ScheduleElbowArrow.png";
import { StepHeader, Empty, CountPill, StatusPill } from "../primitives";

// The two milestones that are anchored to the project window rather than to
// activities — there is nothing to inspect against them, so they are left out.
const EXCLUDED_TYPES = new Set([
  "PROJECT_INITIAL_DEPOSIT",
  "DESIGN_INITIAL_DEPOSIT",
  "PROJECT_WARRANTY_COMPLETION",
]);
const EXCLUDED_NAMES = ["initial deposit", "warranty"];

const isExcluded = (group) =>
  EXCLUDED_TYPES.has(String(group.type || "").toUpperCase()) ||
  EXCLUDED_NAMES.some((name) =>
    String(group.name || "")
      .toLowerCase()
      .includes(name)
  );

const dateText = (value) => (value ? moment(value).format("DD MMM, YYYY") : "-");

// Grey "Due" / "Amount" caption with its dark value beside it.
const metaLabelStyle = { color: "#949daa" };
const metaValueStyle = { color: "#0d0d0e", fontWeight: 500, marginLeft: "4px" };

// The milestone / activity strip both rows share.
const stripStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
  backgroundColor: "#F9FAFB",
  borderRadius: "12px",
  padding: "12px 16px",
};

const metaRowStyle = {
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: "24px",
  flexWrap: "wrap",
  fontSize: "14px",
};

/**
 * Inspection plan — the mapped milestones from the previous step, read as a
 * nested list: each billing milestone expands to the activities that complete
 * it, which is what an inspection is booked against.
 *
 * Read-only: it presents the mapping made in step 2, it does not change it.
 */
const InspectionPlanStep = ({ schedule, preview }) => {
  const activityById = useMemo(
    () => new Map((schedule.activities || []).map((a) => [a.id, a])),
    [schedule.activities]
  );

  // Dates land where the preview says they land, not where they are stored —
  // the activity dates from step 1 are not saved yet.
  const projected = useMemo(
    () =>
      new Map(
        (preview?.plan?.activities || []).map((a) => [String(a.id), a.to])
      ),
    [preview]
  );

  const activityDates = (activity) => {
    const plan = projected.get(String(activity.id));
    return {
      start: plan ? plan.start_date : activity.start_date,
      end: plan ? plan.end_date : activity.end_date,
    };
  };

  const milestones = useMemo(
    () => (schedule.billing_milestones || []).filter((g) => !isExcluded(g)),
    [schedule.billing_milestones]
  );

  const [open, setOpen] = useState({});
  const toggle = (id) =>
    setOpen((prev) => ({ ...prev, [id]: prev[id] === false ? true : !prev[id] }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <StepHeader
        title="Inspection Plan"
      />

      {!milestones.length ? (
        <Empty>No milestones to inspect on this scope.</Empty>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {milestones.map((group) => {
            const linked = (group.activity_ids || [])
              .map((id) => activityById.get(id))
              .filter(Boolean);
            const isOpen = open[group.group_id] !== false;

            return (
              <div
                key={group.group_id}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {/* -------- milestone row -------- */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(group.group_id)}
                  onKeyDown={(e) => e.key === "Enter" && toggle(group.group_id)}
                  style={{ ...stripStyle, cursor: "pointer" }}
                >
                  <MdOutlineExpandMore
                    size={20}
                    style={{
                      color: "#6B7280",
                      transition: "transform 0.15s",
                      transform: isOpen ? "none" : "rotate(-90deg)",
                    }}
                  />
                  <span style={{ color: "#0d0d0e", fontWeight: 500 }}>
                    {group.name}
                  </span>
                  <StatusPill status={group.status} />

                  <div style={metaRowStyle}>
                    <span style={metaLabelStyle}>
                      Due{" "}
                      <span style={metaValueStyle}>
                        {dateText(group.due_date)}
                      </span>
                    </span>
                    {group.amount != null && (
                      <span style={metaLabelStyle}>
                        Amount{" "}
                        <span
                          style={{
                            ...metaValueStyle,
                            display: "inline-flex",
                            verticalAlign: "middle",
                          }}
                        >
                          {formatAmountWithCurrency(group.amount, "black-xs-small")}
                        </span>
                      </span>
                    )}
                    <CountPill tone={linked.length ? "green" : "gray"}>
                      {linked.length} attached
                    </CountPill>
                  </div>
                </div>

                {/* -------- nested activities -------- */}
                {isOpen && (
                  <div
                    style={{
                      paddingLeft: "24px",
                      marginTop: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {!linked.length ? (
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#949daa",
                          paddingLeft: "32px",
                          paddingTop: "8px",
                          paddingBottom: "8px",
                          margin: 0,
                        }}
                      >
                        No activities mapped to this milestone.
                      </p>
                    ) : (
                      linked.map((activity) => {
                        const { start, end } = activityDates(activity);
                        return (
                          <div
                            key={activity.id}
                            style={{ position: "relative", paddingLeft: "32px" }}
                          >
                            {/* Rounded elbow joining the child to its parent.
                                One image only — drawing a separate vertical
                                rule alongside it doubled the line. */}
                            <img
                              src={ArrowElbow}
                              alt=""
                              aria-hidden="true"
                              style={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                height: "calc(50% + 2px)",
                                width: "24px",
                                objectFit: "fill",
                              }}
                            />
                            <div style={stripStyle}>
                              <span style={{ color: "#0d0d0e" }}>
                                {activity.name}
                              </span>
                              <StatusPill status={activity.status} />
                              <div style={metaRowStyle}>
                                <span style={metaLabelStyle}>
                                  Start{" "}
                                  <span style={metaValueStyle}>
                                    {dateText(start)}
                                  </span>
                                </span>
                                <span style={metaLabelStyle}>
                                  End{" "}
                                  <span style={metaValueStyle}>
                                    {dateText(end)}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InspectionPlanStep;
